/**
 * Custom Cart Upsell Component
 * Handles the "Complete your look" upsell section in the cart drawer
 * Displays random products from the "Cart Upsell" collection (handle: cart-upsell)
 */

class CartUpsell {
  constructor(cartDrawerElement) {
    this.cartDrawer = cartDrawerElement;
    this.init();
    this.observeCartUpdates();
  }

  init() {
    this.syncUpsellPrices();
    this.preventSelectValidation();
    this.attachAddToCartHandler();
  }

  observeCartUpdates() {
    // Watch for cart drawer content changes and re-initialize when content updates
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // Check if upsell elements were added/changed
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          const hasUpsellContent = Array.from(mutation.addedNodes).some(node => 
            node.nodeType === 1 && (
              node.classList?.contains('cart-drawer__upsell') ||
              node.querySelector?.('.cart-drawer__upsell')
            )
          );
          
          if (hasUpsellContent) {
            console.log('Cart content updated, re-initializing validation');
            this.syncUpsellPrices();
            this.preventSelectValidation();
          }
        }
      });
    });

    // Observe the cart drawer's main content area
    const cartDrawerInner = this.cartDrawer.querySelector('.drawer__inner');
    if (cartDrawerInner) {
      observer.observe(cartDrawerInner, {
        childList: true,
        subtree: true
      });
    }
  }

  syncUpsellPrices() {
    const upsellSelects = this.cartDrawer.querySelectorAll('[data-upsell-variant-select]');

    upsellSelects.forEach((select) => {
      this.updateUpsellPrice(select);
    });
  }

  updateUpsellPrice(select) {
    const selectedOption = select.options[select.selectedIndex];
    const upsellRoot = select.closest('.cart-drawer__upsell');
    const priceContainer = upsellRoot?.querySelector('[data-upsell-price-container]');
    const priceElement = upsellRoot?.querySelector('[data-upsell-price]');
    const comparePriceElement = upsellRoot?.querySelector('[data-upsell-compare-price]');

    if (!selectedOption || !priceContainer || !priceElement || !comparePriceElement) {
      return;
    }

    const formattedPrice = selectedOption.dataset.price || '';
    const formattedCompareAtPrice = selectedOption.dataset.compareAtPrice || '';
    const isOnSale = Boolean(formattedCompareAtPrice);

    priceElement.textContent = formattedPrice;
    comparePriceElement.textContent = formattedCompareAtPrice;
    comparePriceElement.classList.toggle('hidden', !isOnSale);
    priceContainer.classList.toggle('cart-drawer__upsell-price--on-sale', isOnSale);
  }

  preventSelectValidation() {
    // Prevent validation on upsell select elements with aggressive approach
    const upsellSelects = this.cartDrawer.querySelectorAll('[data-upsell-variant-select]');
    
    upsellSelects.forEach(select => {
      // Store original attributes to prevent them from being added
      const preventValidation = () => {
        select.removeAttribute('min');
        select.removeAttribute('max');
        select.removeAttribute('step');
        select.removeAttribute('pattern');
        select.removeAttribute('required');
        select.removeAttribute('data-min');
        select.removeAttribute('data-max');
        select.removeAttribute('data-step');
        select.setCustomValidity('');
      };
      
      // Initial cleanup
      preventValidation();
      
      // Watch for any attribute changes and remove them
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes') {
            const attrName = mutation.attributeName;
            if (['min', 'max', 'step', 'pattern', 'required', 'data-min', 'data-max', 'data-step'].includes(attrName)) {
              preventValidation();
            }
          }
        });
      });
      
      observer.observe(select, {
        attributes: true,
        attributeOldValue: true
      });
      
      // Prevent any input/change validation
      select.addEventListener('input', (e) => {
        e.target.setCustomValidity('');
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }, { capture: true });
      
      // Prevent validation message on change
      select.addEventListener('change', (e) => {
        e.target.setCustomValidity('');
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }, { capture: true });
      
      // Prevent invalid event completely
      select.addEventListener('invalid', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        e.target.setCustomValidity('');
        return false;
      }, { capture: true });
      
      // Override validation methods
      select.reportValidity = function() {
        return true;
      };
      
      select.checkValidity = function() {
        return true;
      };
      
      // Intercept before input event that might trigger validation
      select.addEventListener('beforeinput', (e) => {
        e.stopPropagation();
        e.stopImmediatePropagation();
      }, { capture: true });
      
      // Prevent blur validation
      select.addEventListener('blur', (e) => {
        e.target.setCustomValidity('');
        e.stopPropagation();
      }, { capture: true });
      
      // Prevent focus validation
      select.addEventListener('focus', (e) => {
        e.target.setCustomValidity('');
        e.stopPropagation();
      }, { capture: true });
    });
    
    // Add global capture listeners to prevent any validation on upsell selects
    const preventUpsellValidation = (e) => {
      if (e.target.hasAttribute('data-upsell-variant-select') || 
          e.target.hasAttribute('data-upsell-select') ||
          e.target.closest('.cart-drawer__upsell-variant-wrapper')) {
        if ((e.type === 'change' || e.type === 'input') && e.target.matches('[data-upsell-variant-select]')) {
          this.updateUpsellPrice(e.target);
        }

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        if (e.target.setCustomValidity) {
          e.target.setCustomValidity('');
        }
        return false;
      }
    };
    
    // Catch all possible validation triggers
    ['invalid', 'change', 'input', 'blur'].forEach(eventType => {
      document.addEventListener(eventType, preventUpsellValidation, { capture: true });
    });
  }

  attachAddToCartHandler() {
    // Use event delegation on the cart-drawer element itself
    // This way the handler persists even when content is updated
    if (this.handlerAttached) {
      return; // Already attached
    }
    
    this.handlerAttached = true;
    
    this.cartDrawer.addEventListener('click', async (event) => {
      // Check if the clicked element is the upsell button or inside it
      const button = event.target.closest('[data-upsell-add-to-cart]');
      if (!button) return;
      
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      
      // Prevent multiple clicks
      if (button.disabled || button.classList.contains('loading')) {
        console.log('Button already processing, ignoring click');
        return;
      }
      
      const variantSelect = this.cartDrawer.querySelector('[data-upsell-variant-select]');
      let variantId = button.dataset.variantId;
      
      // If product has variants, get the selected variant from dropdown
      if (variantSelect) {
        variantId = variantSelect.value;
      }
      
      if (!variantId) {
        console.error('No variant ID found');
        return;
      }
      
      console.log('Adding variant to cart:', variantId);
      
      // Disable button and show loading state
      button.disabled = true;
      button.classList.add('loading');
      
      try {
        const cartRoot = window.Shopify?.routes?.root || '/';
        const addUrl = cartRoot + (cartRoot.endsWith('/') ? '' : '/') + 'cart/add.js';
        
        console.log('Fetching:', addUrl);
        
        const response = await fetch(addUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            items: [{
              id: parseInt(variantId, 10),
              quantity: 1
            }]
          })
        });
        
        const data = await response.json();
        console.log('Response:', response.status, data);
        
        if (response.ok) {
          console.log('Product added successfully, refreshing cart');
          
          // Refresh cart drawer content
          const cartResponse = await fetch('/?section_id=cart-drawer');
          const text = await cartResponse.text();
          const html = document.createElement('div');
          html.innerHTML = text;
          
          const newCartDrawer = html.querySelector('#CartDrawer');
          if (newCartDrawer) {
            const currentCartDrawer = document.querySelector('#CartDrawer');
            if (currentCartDrawer) {
              currentCartDrawer.innerHTML = newCartDrawer.innerHTML;
              console.log('Cart drawer content updated');
              
              // Re-run validation prevention for new content
              // The MutationObserver will catch this, but we can also do it explicitly
              this.syncUpsellPrices();
              this.preventSelectValidation();
            }
          }
          
          // Update cart icon bubble
          const cartIconBubble = html.querySelector('#cart-icon-bubble');
          if (cartIconBubble) {
            const currentBubble = document.querySelector('#cart-icon-bubble');
            if (currentBubble) {
              currentBubble.innerHTML = cartIconBubble.innerHTML;
              console.log('Cart icon updated');
            }
          }
        } else {
          console.error('Add to cart failed:', data);
          throw new Error(data.description || 'Failed to add product to cart');
        }
      } catch (error) {
        console.error('Error adding product to cart:', error);
        button.classList.remove('loading');
        button.disabled = false;
        
        // Show error briefly
        const buttonText = button.querySelector('.cart-drawer__upsell-button-text');
        
        if (buttonText) {
          const originalText = buttonText.textContent;
          buttonText.textContent = 'Error';
          
          setTimeout(() => {
            buttonText.textContent = originalText;
          }, 2000);
        }
      }
    }, { capture: true });
  }
}

// Initialize cart upsell when cart drawer is ready
// Ensure only one instance is created globally
if (!window.cartUpsellInstance) {
  const initializeCartUpsell = () => {
    const cartDrawerElement = document.querySelector('cart-drawer');
    if (cartDrawerElement && !window.cartUpsellInstance) {
      console.log('Initializing CartUpsell');
      window.cartUpsellInstance = new CartUpsell(cartDrawerElement);
    }
  };

  if (customElements.get('cart-drawer')) {
    // CartDrawer is already defined, initialize immediately
    initializeCartUpsell();
  } else {
    // Wait for CartDrawer to be defined
    window.addEventListener('DOMContentLoaded', () => {
      initializeCartUpsell();
    });
  }
}
